import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../components/ui/Icon';
import { MobileSheet } from '../../../components/ui/MobileSheet';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { toast } from '../../../stores/toast.store';
import { fmtEur } from '../../../lib/utils';
import { giorniDaOggi, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, rataInParole, type RataDaIncassare } from './fattureApi';

/**
 * "Sollecita": prepara il testo di un sollecito, pronto da copiare in una mail
 * o in WhatsApp. NON manda niente: chi sollecita sceglie a chi e come (la mail
 * del cliente può non essere quella giusta, e un sollecito partito per sbaglio
 * non si ritira).
 *
 * Il testo parla al plurale ("vi scriviamo", "il team Masamune"), senza
 * trattini, e dice cosa fare se la fattura è già stata pagata.
 *
 * Sul computer è un popover sotto il bottone, sul telefono sale dal basso
 * (mobile.md §6).
 */
type Canale = 'mail' | 'whatsapp';

function scadenzaInFrase(r: RataDaIncassare): string {
  const d = giorniDaOggi(r.dueDate);
  const giorno = giornoInParole(r.dueDate);
  if (d == null) return 'risulta ancora da saldare';
  if (d < 0) return `è scaduta il ${giorno} e dai nostri conti risulta ancora da saldare`;
  if (d === 0) return 'scade oggi';
  return `scade il ${giorno}: ve lo ricordiamo per tempo`;
}

export function testoSollecito(r: RataDaIncassare, canale: Canale): string {
  const numero = r.invoiceNumber ?? '';
  const rata = rataInParole(r.seq, r.ofTotal);
  const importo = `€ ${fmtEur(r.amount, true)}`;
  const emessa = r.issueDate ? ` ${delGiorno(r.issueDate)}` : '';
  const cosa = rata ? `la ${rata} della fattura n. ${numero}${emessa}, di ${importo},` : `la fattura n. ${numero}${emessa}, di ${importo},`;
  const scaduta = (giorniDaOggi(r.dueDate) ?? 0) < 0;

  if (canale === 'whatsapp') {
    return [
      `Buongiorno, vi scriviamo per ${cosa} che ${scadenzaInFrase(r)}.`,
      scaduta ? "Se l'avete già pagata, mandateci pure la ricevuta del bonifico così la segniamo subito." : '',
      'Grazie, il team Masamune',
    ].filter(Boolean).join(' ');
  }
  return [
    `Oggetto: fattura n. ${numero} ${scaduta ? 'ancora da saldare' : 'in scadenza'}`,
    '',
    'Buongiorno,',
    `vi scriviamo per ${cosa} che ${scadenzaInFrase(r)}.`,
    scaduta
      ? "Se l'avete già pagata potete ignorare questo messaggio: vi chiediamo solo di mandarci la ricevuta del bonifico, così la segniamo subito."
      : 'Trovate tutti i dati per il pagamento nella fattura.',
    '',
    'Grazie,',
    'il team Masamune',
  ].join('\n');
}

async function copia(testo: string, area: HTMLTextAreaElement | null): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(testo);
    return true;
  } catch {
    // Senza permesso sugli appunti (http, iframe): si seleziona il testo e si usa il vecchio comando.
    if (!area) return false;
    area.select();
    try { return document.execCommand('copy'); } catch { return false; }
  }
}

export function SollecitaPopover({ rata, onClose }: { rata: RataDaIncassare; onClose: () => void }) {
  const mobile = useIsMobile();
  const [canale, setCanale] = useState<Canale>('mail');
  const iniziale = useMemo(() => testoSollecito(rata, canale), [rata, canale]);
  const [testo, setTesto] = useState(iniziale);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { setTesto(iniziale); }, [iniziale]);

  useEffect(() => {
    if (mobile) return; // il pannello del telefono gestisce Esc da sé
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, mobile]);

  const onCopia = async () => {
    const ok = await copia(testo, area.current);
    if (ok) {
      toast.ok('Testo del sollecito copiato', canale === 'mail' ? 'Incollalo in una nuova mail al cliente.' : 'Incollalo nella chat WhatsApp del cliente.');
      onClose();
    } else {
      toast.alert('Il testo non si è copiato', 'Selezionalo e copialo a mano.');
    }
  };

  const corpo = (
    <>
      <div className="tweak-seg mb-3" role="group" aria-label="Dove lo mandi">
        <button aria-pressed={canale === 'mail'} onClick={() => setCanale('mail')}><Icon name="mail" size={12} /> Testo per una mail</button>
        <button aria-pressed={canale === 'whatsapp'} onClick={() => setCanale('whatsapp')}><Icon name="message" size={12} /> Testo per WhatsApp</button>
      </div>
      <textarea
        ref={area}
        className="inp fs-sollecito-testo"
        aria-label="Testo del sollecito"
        value={testo}
        onChange={(e) => setTesto(e.target.value)}
      />
      <div className="text-[12px] mute mt-2" style={{ lineHeight: 1.45 }}>
        Da qui non parte niente: copi il testo e lo mandi tu, dalla mail o da WhatsApp.
      </div>
    </>
  );
  const bottoni = (
    <>
      <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
      <div className="spacer" />
      <button className="btn btn-sm btn-primary" onClick={onCopia}>
        <Icon name="copy" size={10} /> Copia il testo
      </button>
    </>
  );

  if (mobile) {
    return (
      <MobileSheet open onClose={onClose} title={`Sollecito per la fattura ${rata.invoiceNumber ?? ''}`}
        footer={<button className="btn btn-primary m-block" onClick={onCopia}><Icon name="copy" size={12} /> Copia il testo</button>}>
        {corpo}
      </MobileSheet>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div className="dropdown adm-pop right-0 mt-1 z-[61]" role="dialog" aria-label="Prepara il sollecito"
        style={{ width: 'min(480px, calc(100vw - 32px))' }}>
        <div className="adm-pop-hd">
          <span className="t-micro">Prepara il sollecito</span>
          <span className="mono font-semibold truncate">{rata.invoiceNumber}</span>
        </div>
        <div className="px-4 py-3">{corpo}</div>
        <div className="adm-pop-ft">{bottoni}</div>
      </div>
    </>
  );
}
